import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '../../../redux/hooks';
import {
  AvailableIcon,
  AvailableUserListIcon,
  CallerIcon,
  DurationIcon,
  ExtensionIcon,
  FilterIcon,
  IncomingCallsIcon,
  LoggedOutIcon,
  OnCallIcon,
  OutgoingCallsIcon,
  ParkedCallsIcon,
  TotalCallsIcon,
  TotalUsersIcon,
  UserListIcon,
  WaitingIcon,
} from '../../../assets';
import { Accordion, Loader, SearchBar, Typography } from '../../common';
import DefaultLayout from '../../layout/DefaultLayout';
import MiniCard from './MiniCard';
import CallItemCard from './CallItemCard';
import colors from '../../../styles/colors.module.scss';
import { RootState } from 'redux/store';
import { useInfoStatus } from 'hooks/useInfoStatus';
import { LiveCall } from 'redux/slices/liveCalls.slice';
import { useCallTimers } from 'hooks/useCallTimers';
import useLiveActivityData from 'hooks/useLiveActivityData';
import { useCallSocket } from 'hooks/useCallSocket';
import InfoModal from './InfoDrawer';
import { phoneNumberFormatE164 } from 'utils/phoneNumberFormatE164';
import { CallHistoryRecord } from 'redux/slices/callHistory.slice';
import { useHistoryLookups } from 'hooks/useHistoryLookups';
import './liveCalls.scss';
import LiveCallItemWrapper from './LiveCallsItemWrapper';
import { useSortingAccordion } from 'hooks/useSortingAccordion';

type StatusKey = 'Ringing' | 'On Call' | 'Available' | 'Logged Out';

const statusIconMap: Record<StatusKey, string> = {
  Ringing: WaitingIcon,
  'On Call': OnCallIcon,
  Available: AvailableIcon,
  'Logged Out': LoggedOutIcon,
};

const LiveCallsDefault = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCall, setSelectedCall] = useState<LiveCall | null>(null);
  const { handleRecord } = useCallSocket();
  const { calculateTimer } = useCallTimers();
  const callHistory = useAppSelector(state => state.callHistory.data);
  const [callHistoryData, setCallHistoryData] = useState<CallHistoryRecord[]>([]);
  const { items: agents, status, error } = useAppSelector((state) => state?.agents);
  const { items: ivrMenus } = useAppSelector((state) => state?.ivrMenus);
  const { items: queues } = useAppSelector((state) => state?.queues);


  const calls = useAppSelector((state: RootState) => state?.liveCalls?.calls);
  const [agentSearch, setAgentSearch] = useState('');
  const [callSearch, setCallSearch] = useState('');
  const [callActions, setCallActions] = useState<Record<string, string[]>>({});
  const { incoming, outgoing, parked, totalCalls, totalUsers } = useLiveActivityData(calls, agents);
  const {
    sortOrder,
    selectedTitle,
    menuItems,
    setSelectedTitle,
  } = useSortingAccordion();

  const miniCardsData = useMemo(() => [
    {
      value: incoming.toString(),
      heading: 'Incoming',
      iconClassName: IncomingCallsIcon,
      containerBg: colors.lightGreen,
      imageContainerBg: colors.darkGreen,
    },
    {
      value: outgoing.toString(),
      heading: 'Outgoing',
      iconClassName: OutgoingCallsIcon,
      containerBg: colors.lightPurple,
      imageContainerBg: colors.darkPurple,
    },
    {
      value: parked.toString(),
      heading: 'Parked',
      iconClassName: ParkedCallsIcon,
      containerBg: colors.lightMaroon,
      imageContainerBg: colors.darkMaroon,
    },
    {
      value: totalCalls.toString(),
      heading: 'Total Calls',
      iconClassName: TotalCallsIcon,
      containerBg: colors.lightOrange,
      imageContainerBg: colors.darkOrange,
    },
    {
      value: totalUsers.toString(),
      heading: 'Total Users',
      iconClassName: TotalUsersIcon,
      containerBg: colors.lightCyan,
      imageContainerBg: colors.darkCyan,
    },
  ], [incoming, outgoing, parked, totalCalls, totalUsers]);

  const { fetchCallHistory } = useHistoryLookups();

  useEffect(() => {
    if (!selectedCall) return;

    const matchingHistory = callHistory.find(
      (item) => item.id === selectedCall.callUUID
    );

    if (matchingHistory?.records) {
      setCallHistoryData(matchingHistory.records);
    } else {
      setCallHistoryData([]);
    }
  }, [callHistory, selectedCall]);



  const handleCancel = () => {
    setModalOpen(false);
  };

  const handleCallClick = async (call: LiveCall) => {
    setSelectedCall(call);
    setModalOpen(true);
    try {
      await fetchCallHistory(call);
    } catch (err) {
      console.error("❌ Failed to fetch call history:", err);
    }
  };

  const handleActionSelect = useCallback((callId: string, actionKey: string) => {
    const call = calls.find(c => c.callUUID === callId);
    if (!call) return;

    if (actionKey === '2') {
      const isRecording = call?.isRecording;
      handleRecord(callId, isRecording ? 'stop' : 'start');
      return;
    }

    setCallActions(prev => {
      const current = prev[callId] || [];
      if (current.includes(actionKey)) return prev;
      return { ...prev, [callId]: [...current, actionKey] };
    });
  }, [calls, handleRecord]);

  const handleHangUp = useCallback((callId: string) => {
    // Placeholder for hang-up logic
  }, []);

  const filteredAgents = useMemo(() => {
    if (!Array.isArray(agents)) return [];

    return agents.map(agent => {
      const status = (agent.status as StatusKey) || 'Available';
      const activeCall = calls?.find(call =>
        call?.callees?.some(callee =>
          callee?.number === agent?.extension && callee?.answerState === 'answered'
        )
      );

      return {
        id: agent?.id || '',
        name: agent?.name?.trim() || agent?.extension || 'Unknown',
        ext: agent?.extension || '',
        status,
        phone: activeCall?.callerIDNumber || '',
        callerName: activeCall?.callerIDName || '',
        duration: activeCall?.startTime ?? '',
        icons: {
          user: UserListIcon,
          ext: ExtensionIcon,
          caller: CallerIcon,
          duration: DurationIcon,
          status: statusIconMap[status] || AvailableIcon,
        },
      };
    }).filter(user =>
      user.status.toLowerCase().includes(agentSearch.toLowerCase()) ||
      user.ext.toLowerCase().includes(agentSearch.toLowerCase())
    ).sort((a, b) =>
      sortOrder === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name)
    );
  }, [agents, agentSearch, sortOrder, calls]);

  const filteredCalls = useMemo(() => {
    if (!Array.isArray(calls)) return [];
    return calls.filter(call =>
      typeof call?.callerIDName === 'string' &&
      call?.callerIDName.toLowerCase().includes(callSearch.toLowerCase())
    );
  }, [calls, callSearch]);

  const activeCallee = selectedCall?.callees?.find(c => c.answerState === 'answered');
  const agent = agents.find(a => a.extension === activeCallee?.number);



  const hhaData = useAppSelector((state) => state.hhaexchange.items);

  const matchedHhaData = useMemo(() => {
    if (!selectedCall?.callerIDNumber) return undefined;

    const formatted = phoneNumberFormatE164(selectedCall.callerIDNumber);

    const match = hhaData.find(item =>
      item.HomePhone === formatted ||
      item.Phone2 === formatted ||
      item.Phone3 === formatted
    );

    return match;
  }, [selectedCall?.callerIDNumber, hhaData]);

  const isLoading = useAppSelector((state) => state.callHistory.loading);

  const renderMiniCards = (): JSX.Element[] => {
    return miniCardsData.map((card, idx) => <MiniCard key={idx} {...card} />);
  };

  const renderLiveCallsList = (): JSX.Element => {
    return filteredCalls.length === 0 ? (
      <div className="no-calls-text">
        <Typography variant="text-1" className="text-center">
          No Active Calls found
        </Typography>
      </div>
    ) : (
      <ul className="active-calls-list-container">
        {filteredCalls.map(call => (
          <LiveCallItemWrapper
            key={call.callUUID}
            call={call}
            actions={callActions[call.callUUID] || []}
            onSelectAction={(actionKey) => handleActionSelect(call.callUUID, actionKey)}
            onHangUp={() => handleHangUp(call.callUUID)}
            onClickCall={() => handleCallClick(call)}
          />
        ))}
      </ul>
    );
  };

  const renderAgentList = (): JSX.Element => {
    if (status === 'loading') {
      return (
        <div className="loader">
          <Loader />
        </div>
      );
    }

    if (filteredAgents.length === 0) {
      return (
        <Typography variant="text-1" className="text-center">
          No agents found
        </Typography>
      );
    }

    return (
      <>
        {filteredAgents.map(agent => (
          <li className="user-list-item" key={agent.id}>
            <div className="image">
              <img src={agent.icons.user} alt="User Icon" className="agent-image" />
            </div>
            <div className="user-name">
              {agent.status === 'On Call' ? (
                <div className="name-number">
                  <Typography variant="subHeading-1" className="agent-name">
                    {agent.name}
                  </Typography>
                  <Typography variant="small-text-3" className="agent-phone">
                    {agent.phone || 'Unknown'}
                  </Typography>
                </div>
              ) : (
                <Typography variant="subHeading-1" className="agent-name">
                  {agent.name}
                </Typography>
              )}
            </div>
            <div className="call-info">
              {agent.status === 'On Call' ? (
                <div className="caller-name-duration-container">
                  <div className="caller-name">
                    <img src={agent.icons.caller} alt="Caller Icon" />
                    <Typography variant="small-text-3">{agent.callerName}</Typography>
                  </div>
                  <div className="duration">
                    <img src={agent.icons.duration} alt="Duration Icon" />
                    <Typography variant="small-text-3" className="duration">
                      {calculateTimer(agent.duration)}
                    </Typography>
                  </div>
                </div>
              ) : (
                <div className="ext-name">
                  <img src={agent.icons.ext} alt="Ext icon" />
                  <Typography variant="small-text-3">{agent.ext}</Typography>
                </div>
              )}
            </div>
            <div className="status">
              <img src={agent.icons.status} alt="Status Icon" />
              <Typography variant="text-2">{agent.status}</Typography>
            </div>
          </li>
        ))}
      </>
    );
  };



  return (
    <DefaultLayout>
      <div className="live-calls-container">
        <div className="live-calls-body-container">
          <div className="mini-cards-container">
            {miniCardsData.map((card, idx) => <MiniCard key={idx} {...card} />)}
          </div>


          <div className="user-list-active-calls-container">
            <div className="users-list-container">
              <div className="user-list-heading">
                <img src={AvailableUserListIcon} alt="Available user list icon" />
                <Typography variant="h3-bold">Available User Online</Typography>
              </div>

              <div className="sorting-accordion">
                <Accordion
                  labelName="Filter By"
  isTitleStatic={false}
  theme="dark"
  menuItems={menuItems}
  selectedTitle={selectedTitle}
  onSelectedTitleChange={setSelectedTitle}
                />
              </div>

              <div className="searchBar">
                <SearchBar value={agentSearch} onChange={(e) => setAgentSearch(e.target.value)} />
              </div>

              <ul className="list-container" role="list">
                {renderAgentList()}
              </ul>

            </div>

            <div className="active-calls-container">
              <div className="active-calls-header">
                <SearchBar value={callSearch} onChange={(e) => setCallSearch(e.target.value)} />
                <div />
                <div className="filter-bar-container">
                  <img src={FilterIcon} alt="Filter icon" />
                  <Typography variant="text-2">Filters</Typography>
                </div>
              </div>

              {renderLiveCallsList()}

            </div>
          </div>
        </div>
      </div>
      <InfoModal
        open={modalOpen}
        onCancel={handleCancel}
        callerDetails={[
          { label: 'Caller Name', value: selectedCall?.callerIDName || '-' },
          { label: 'Caller ID', value: selectedCall?.callerIDNumber || '-' },
          { label: 'Agent Name', value: agent?.name || '-' },
          { label: 'Agent Extension', value: agent?.extension || '-' },
          { label: 'Phone Number', value: selectedCall?.destination || '-' },
          { label: 'Call Duration', value: calculateTimer(selectedCall?.startTime) },
        ]}
        additionalDetails={[
          { label: 'Caller Type', value: selectedCall?.callDirection || '-' },
          { label: 'Caller Recording', value: selectedCall?.isRecording ? 'Yes' : 'No' },
          { label: 'Intercept Call', value: 'N/A' },
          { label: 'Barge Transfer', value: 'N/A' },
          { label: 'Status', value: selectedCall?.answerState || '-' },
        ]}
        hhaexchangeData={matchedHhaData ? {
          RecordType: matchedHhaData.RecordType,
          ID: matchedHhaData.ID,
          Name: `${matchedHhaData.FirstName ?? ''} ${matchedHhaData.MiddleName ?? ''} ${matchedHhaData.LastName ?? ''}`.trim(),
          Phone: matchedHhaData.HomePhone,
          AssignedTo: matchedHhaData.RecordType === 'Patient' ? matchedHhaData.Coordinators : undefined,
          Company: matchedHhaData.RecordType === 'Caregiver' ? matchedHhaData.Company : undefined,
        } : undefined}
        recentHistory={callHistoryData}
        historyLoading={isLoading}
        agents={agents}
        ivrMenus={ivrMenus}
        queues={queues}
      />



    </DefaultLayout>
  );
};

export default LiveCallsDefault;
